import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { pricingDB, type PricingConfig } from '../../lib/pricingDB';

type FieldGroup = {
  title: string;
  icon: string;
  color: string;
  description: string;
  fields: {
    key: keyof Omit<PricingConfig, 'currency' | 'updated_at'>;
    label: string;
    sublabel: string;
    badge?: string;
    badgeColor?: string;
  }[];
};

const FIELD_GROUPS: FieldGroup[] = [
  {
    title: 'Temel İmplant Ücreti',
    icon: 'ri-coin-line',
    color: 'teal',
    description: 'Her diş için uygulanan sabit temel ücret',
    fields: [
      {
        key: 'base_fee',
        label: 'Temel Ücret',
        sublabel: 'Tüm implantlara uygulanan baz fiyat',
        badge: 'Zorunlu',
        badgeColor: 'teal',
      },
    ],
  },
  {
    title: 'Soket Durumu',
    icon: 'ri-hospital-line',
    color: 'cyan',
    description: 'Taze çekim soketi veya normal alveol durumuna göre ek ücret',
    fields: [
      {
        key: 'soket_e_fee',
        label: 'Soket: E (Taze Çekim)',
        sublabel: 'Taze çekim soketi mevcut — ek cerrahi gerektirir',
        badge: 'E',
        badgeColor: 'cyan',
      },
      {
        key: 'soket_h_fee',
        label: 'Soket: H (Normal)',
        sublabel: 'Çekim yapılmamış, sağlam kemik yapısı',
        badge: 'H',
        badgeColor: 'gray',
      },
    ],
  },
  {
    title: 'Greftleme',
    icon: 'ri-first-aid-kit-line',
    color: 'emerald',
    description: 'Kemik greftleme uygulanıp uygulanmadığına göre ek ücret',
    fields: [
      {
        key: 'greft_e_fee',
        label: 'Greft: E (Greftleme Var)',
        sublabel: 'Kemik greftleme uygulandı — materyal + işlem dahil',
        badge: 'E',
        badgeColor: 'emerald',
      },
      {
        key: 'greft_h_fee',
        label: 'Greft: H (Greft Yok)',
        sublabel: 'Greftleme gerekmedi, standart prosedür',
        badge: 'H',
        badgeColor: 'gray',
      },
    ],
  },
  {
    title: 'Kemik Kalitesi',
    icon: 'ri-bar-chart-grouped-line',
    color: 'amber',
    description: 'Alveoler kemik yoğunluğuna göre değişen işlem güçlüğü ücreti',
    fields: [
      {
        key: 'kemik_1_fee',
        label: 'Kemik: 1 (İdeal)',
        sublabel: 'Yüksek yoğunluklu, ideal kemik yapısı',
        badge: '1',
        badgeColor: 'green',
      },
      {
        key: 'kemik_2_fee',
        label: 'Kemik: 2 (Orta)',
        sublabel: 'Orta yoğunluk, standart protokol',
        badge: '2',
        badgeColor: 'amber',
      },
      {
        key: 'kemik_3_fee',
        label: 'Kemik: 3 (Düşük)',
        sublabel: 'Düşük yoğunluk, ek prosedür gerekebilir',
        badge: '3',
        badgeColor: 'red',
      },
    ],
  },
  {
    title: 'Hacim',
    icon: 'ri-3d-cube-sphere-line',
    color: 'violet',
    description: 'İmplant hacmine göre uygulanan fiyat farkı',
    fields: [
      {
        key: 'hacim_z_fee',
        label: 'Hacim: Z (Standart)',
        sublabel: 'Standart hacimde implant uygulaması',
        badge: 'Z',
        badgeColor: 'slate',
      },
      {
        key: 'hacim_y_fee',
        label: 'Hacim: Y (Geniş)',
        sublabel: 'Geniş hacimli implant, daha fazla materyal',
        badge: 'Y',
        badgeColor: 'violet',
      },
    ],
  },
  {
    title: 'Healing',
    icon: 'ri-nurse-line',
    color: 'rose',
    description: 'Healing abutment uygulanıp uygulanmadığına göre ek ücret',
    fields: [
      {
        key: 'healing_e_fee',
        label: 'Healing: E (Var)',
        sublabel: 'Healing abutment uygulandı',
        badge: 'E',
        badgeColor: 'rose',
      },
      {
        key: 'healing_h_fee',
        label: 'Healing: H (Yok)',
        sublabel: 'Healing abutment uygulanmadı',
        badge: 'H',
        badgeColor: 'gray',
      },
    ],
  },
];

const BADGE_COLORS: Record<string, string> = {
  teal: 'bg-teal-100 text-teal-700',
  cyan: 'bg-cyan-100 text-cyan-700',
  gray: 'bg-gray-100 text-gray-600',
  emerald: 'bg-emerald-100 text-emerald-700',
  green: 'bg-green-100 text-green-700',
  amber: 'bg-amber-100 text-amber-700',
  red: 'bg-red-100 text-red-700',
  slate: 'bg-slate-100 text-slate-600',
  violet: 'bg-violet-100 text-violet-700',
  rose: 'bg-rose-100 text-rose-700',
};

const ICON_COLORS: Record<string, string> = {
  teal: 'bg-teal-50 text-teal-600',
  cyan: 'bg-cyan-50 text-cyan-600',
  emerald: 'bg-emerald-50 text-emerald-600',
  amber: 'bg-amber-50 text-amber-600',
  violet: 'bg-violet-50 text-violet-600',
  rose: 'bg-rose-50 text-rose-600',
};

const CURRENCIES = ['₺', '$', '€', '£'];

export default function PricingAdmin() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<PricingConfig>(() => pricingDB.get());
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showReset, setShowReset] = useState(false);

  useEffect(() => {
    pricingDB.load().then(setConfig);
  }, []);

  // Preview state
  const [previewSoket, setPreviewSoket] = useState<'e' | 'h'>('e');
  const [previewGreft, setPreviewGreft] = useState<'e' | 'h'>('e');
  const [previewKemik, setPreviewKemik] = useState<'1' | '2' | '3'>('2');
  const [previewHacim, setPreviewHacim] = useState<'z' | 'y'>('z');
  const [previewHealing, setPreviewHealing] = useState<'e' | 'h'>('h');

  const [lastSaved, setLastSaved] = useState<string>('');

  useEffect(() => {
    if (config.updated_at) {
      setLastSaved(new Date(config.updated_at).toLocaleString('tr-TR'));
    }
  }, [config.updated_at]);

  const handleChange = (key: keyof Omit<PricingConfig, 'currency' | 'updated_at'>, raw: string) => {
    const val = parseInt(raw.replace(/\D/g, ''), 10);
    setConfig((prev) => ({ ...prev, [key]: isNaN(val) ? 0 : val }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await pricingDB.save(config);
      setConfig(result);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    const reset = await pricingDB.reset();
    setConfig(reset);
    setShowReset(false);
  };

  const previewFee = pricingDB.calcToothFee(config, {
    soket: previewSoket,
    greft: previewGreft,
    kemik: previewKemik,
    hacim: previewHacim,
    healing: previewHealing,
  });

  const formatCurrency = (val: number) =>
    `${config.currency}${val.toLocaleString('tr-TR')}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50">
      {/* Navbar */}
      <nav className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center">
              <i className="ri-price-tag-3-line text-lg text-white"></i>
            </div>
            <div>
              <h1 className="text-base font-bold text-gray-900">Ücret Yönetimi</h1>
              <p className="text-xs text-gray-500">Ameliyat ücretlerini parametreye göre ayarla</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {lastSaved && (
              <span className="text-xs text-gray-400 hidden md:block">
                Son kayıt: {lastSaved}
              </span>
            )}
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-2 whitespace-nowrap cursor-pointer text-sm"
            >
              <i className="ri-arrow-left-line"></i>
              Geri
            </button>
          </div>
        </div>
      </nav>

      {saved && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-6 py-3 bg-green-500 text-white rounded-xl flex items-center gap-2 text-sm">
          <i className="ri-check-line text-xl"></i>Fiyatlar kaydedildi!
        </div>
      )}

      <div className="max-w-6xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* LEFT: PRICING FORM */}
          <div className="lg:col-span-2 space-y-5">

            {/* Currency Selector */}
            <div className="bg-white rounded-2xl border border-gray-100 p-5">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-7 h-7 flex items-center justify-center bg-teal-50 rounded-lg">
                  <i className="ri-exchange-dollar-line text-teal-600 text-sm"></i>
                </div>
                <h2 className="text-sm font-bold text-gray-800">Para Birimi</h2>
              </div>
              <div className="flex gap-2">
                {CURRENCIES.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setConfig((prev) => ({ ...prev, currency: c }))}
                    className={`w-12 h-12 rounded-xl text-base font-bold transition-all cursor-pointer ${
                      config.currency === c
                        ? 'bg-teal-500 text-white scale-105'
                        : 'bg-gray-100 text-gray-600 hover:bg-teal-50 hover:text-teal-600'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>

            {/* Field Groups */}
            {FIELD_GROUPS.map((group) => (
              <div key={group.title} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-3">
                  <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${ICON_COLORS[group.color] || 'bg-gray-50 text-gray-600'}`}>
                    <i className={`${group.icon} text-base`}></i>
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-800">{group.title}</h2>
                    <p className="text-xs text-gray-400">{group.description}</p>
                  </div>
                </div>
                <div className="divide-y divide-gray-50">
                  {group.fields.map((field) => (
                    <div key={field.key} className="px-5 py-4 flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          {field.badge && (
                            <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${BADGE_COLORS[field.badgeColor || 'gray']}`}>
                              {field.badge}
                            </span>
                          )}
                          <span className="text-sm font-semibold text-gray-800">{field.label}</span>
                        </div>
                        <p className="text-xs text-gray-400">{field.sublabel}</p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <span className="text-sm font-semibold text-gray-500">{config.currency}</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={config[field.key].toLocaleString('tr-TR')}
                          onChange={(e) => handleChange(field.key, e.target.value)}
                          className="w-28 px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono text-right focus:ring-2 focus:ring-teal-400 focus:border-transparent transition-all"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* Action Buttons */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowReset(true)}
                className="px-5 py-3 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer text-sm flex items-center gap-2"
              >
                <i className="ri-refresh-line"></i>
                Sıfırla
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex-1 py-3 bg-gradient-to-r from-teal-500 to-cyan-600 text-white rounded-xl font-semibold hover:from-teal-600 hover:to-cyan-700 transition-all whitespace-nowrap cursor-pointer text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {saving ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>Kaydediliyor...</>
                ) : (
                  <><i className="ri-save-line text-base"></i>Fiyatları Kaydet</>
                )}
              </button>
            </div>
          </div>

          {/* RIGHT: PREVIEW CALCULATOR */}
          <div className="space-y-5">

            {/* Calculator */}
            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden sticky top-24">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                <div className="w-7 h-7 flex items-center justify-center bg-cyan-50 rounded-lg">
                  <i className="ri-calculator-line text-cyan-600 text-sm"></i>
                </div>
                <h2 className="text-sm font-bold text-gray-800">Ücret Hesaplayıcı</h2>
              </div>

              <div className="p-5 space-y-4">
                {/* Soket */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">Soket Durumu</p>
                  <div className="flex gap-2">
                    {(['e', 'h'] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setPreviewSoket(v)}
                        className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer ${
                          previewSoket === v ? 'bg-cyan-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-cyan-50'
                        }`}
                      >
                        {v.toUpperCase()} {v === 'e' ? '(Taze)' : '(Normal)'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Greft */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">Greftleme</p>
                  <div className="flex gap-2">
                    {(['e', 'h'] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setPreviewGreft(v)}
                        className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer ${
                          previewGreft === v ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-emerald-50'
                        }`}
                      >
                        {v.toUpperCase()} {v === 'e' ? '(Var)' : '(Yok)'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Kemik */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">Kemik Kalitesi</p>
                  <div className="flex gap-2">
                    {(['1', '2', '3'] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setPreviewKemik(v)}
                        className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer ${
                          previewKemik === v ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-50'
                        }`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Hacim */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">Hacim</p>
                  <div className="flex gap-2">
                    {(['z', 'y'] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setPreviewHacim(v)}
                        className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer ${
                          previewHacim === v ? 'bg-violet-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-violet-50'
                        }`}
                      >
                        {v.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Healing */}
                <div>
                  <p className="text-xs font-semibold text-gray-500 mb-2">Healing</p>
                  <div className="flex gap-2">
                    {(['e', 'h'] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setPreviewHealing(v)}
                        className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all cursor-pointer ${
                          previewHealing === v ? 'bg-rose-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-rose-50'
                        }`}
                      >
                        {v.toUpperCase()} {v === 'e' ? '(Var)' : '(Yok)'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Breakdown */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                  <p className="text-xs font-semibold text-gray-500 mb-3">Ücret Dökümü</p>
                  {[
                    { label: 'Temel İmplant', val: config.base_fee },
                    {
                      label: `Soket (${previewSoket.toUpperCase()})`,
                      val: previewSoket === 'e' ? config.soket_e_fee : config.soket_h_fee,
                    },
                    {
                      label: `Greft (${previewGreft.toUpperCase()})`,
                      val: previewGreft === 'e' ? config.greft_e_fee : config.greft_h_fee,
                    },
                    {
                      label: `Kemik (${previewKemik})`,
                      val: previewKemik === '1' ? config.kemik_1_fee : previewKemik === '2' ? config.kemik_2_fee : config.kemik_3_fee,
                    },
                    {
                      label: `Hacim (${previewHacim.toUpperCase()})`,
                      val: previewHacim === 'z' ? config.hacim_z_fee : config.hacim_y_fee,
                    },
                    {
                      label: `Healing (${previewHealing.toUpperCase()})`,
                      val: previewHealing === 'e' ? config.healing_e_fee : config.healing_h_fee,
                    },
                  ].map((row) => (
                    <div key={row.label} className="flex justify-between items-center">
                      <span className="text-gray-500">{row.label}</span>
                      <span className={`font-semibold ${row.val > 0 ? 'text-gray-800' : 'text-gray-400'}`}>
                        {row.val > 0 ? `+${formatCurrency(row.val)}` : '—'}
                      </span>
                    </div>
                  ))}
                  <div className="border-t border-gray-200 pt-3 flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-900">Toplam / Diş</span>
                    <span className="text-lg font-bold text-teal-600">{formatCurrency(previewFee)}</span>
                  </div>
                </div>

                <p className="text-xs text-gray-400 text-center">
                  * Kaydedilmemiş değişiklikler canlı hesaplanır
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Reset Confirm Modal */}
      {showReset && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6">
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-refresh-line text-2xl text-red-500"></i>
            </div>
            <h3 className="text-lg font-bold text-gray-900 text-center mb-2">Varsayılana Sıfırla?</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              Tüm fiyat ayarları varsayılan değerlere döndürülecek. Bu işlem geri alınamaz.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setShowReset(false)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-50 whitespace-nowrap cursor-pointer text-sm"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={handleReset}
                className="flex-1 py-2.5 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600 whitespace-nowrap cursor-pointer text-sm"
              >
                Sıfırla
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
